import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import './CreateConfiguration.css'
import Navbar from '@/components/Navbar'
import HeaderBar from '@/components/HeaderBar'
import DurationInput from '@/components/DurationInput'
import deviceConfigSchema from '@/config/deviceConfigurationSchema.json'
import type { DeviceConfigurationSchema, DeviceConfigurationValues, ConfigurationProperty } from '@/types/deviceConfiguration'
import { parseSchemaToLayout, extractDefaultValues } from '@/utils/configSchemaParser'
import {unflattenConfiguration} from "@/utils/configUnflattener";
import { Helmet } from 'react-helmet-async'
import { PAGES } from '@/constants/pages'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { DurationInputService } from '../services/DurationInputService'
import { Configuration, PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import toast from '@/utils/toast'

const schema = deviceConfigSchema as DeviceConfigurationSchema

interface ConfigurationFormProps {
  initialValues?: DeviceConfigurationValues
  onCancel: () => void
  onSave: (name: string, description: string, values: DeviceConfigurationValues, channelPartnerId?: number) => void
  hasInternalAccess: boolean
}

// Fields restricted to internal SoloProtect users only (hidden from regular PartnerAdministrators)
const INTERNAL_USER_ONLY_FIELDS = [
  'OPTION.SYSTEM.SOFTWARE.UI.HAPTICS_ENABLED',
  'OPTION.SYSTEM.SOFTWARE.MESSAGING.DEFAULT_HEARTBEAT_INTERVAL',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_REPORT_INTERVAL',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BYTES',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_AGE_MS',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_ROTATION_MAX_BACKUPS_COUNT',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_LEVEL',
  'OPTION.SYSTEM.SOFTWARE.LOGGING.LOGGING_DUMP_AT_STARTUP',
  'OPTION.APPLICATION.ALERTS.RED_ALERT.RED_ALERT_RETRY_INTERVAL',
  'OPTION.APPLICATION.ALERTS.CHECK_IN_ALERT.CHECK_IN_ALERT_RETRY_INTERVAL',
  'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_RETRY_INTERVAL',
  'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TIMER_MAXIMUM_VALUE',
  'OPTION.APPLICATION.ALERTS.INCAPACITATION_ALERT.INCAPACITATION_ALERT_TILT_TRIGGER_THRESHOLD_DEGREES'
]

function ConfigurationForm({ initialValues, onCancel, onSave, hasInternalAccess }: ConfigurationFormProps) {
  // Parse schema to get layout and defaults
  const configLayout = useMemo(() => parseSchemaToLayout(schema), [])
  const configDefaults = useMemo(() => extractDefaultValues(schema), [])

  const [configName, setConfigName] = useState('')
  const [configDescription, setConfigDescription] = useState('')
  const [values, setValues] = useState<DeviceConfigurationValues>(
    initialValues || { ...configDefaults }
  )
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const updated = new Set(prev)
      if (updated.has(section)) {
        updated.delete(section)
      } else {
        updated.add(section)
      }
      return updated
    })
  }

  const handleValueChange = (key: string, value: string | number | boolean) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!configName.trim()) {
      alert('Please enter a configuration name')
      return
    }
    onSave(configName, configDescription, values)
  }

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const renderField = (property: ConfigurationProperty) => {
    const value = values[property.key] !== undefined
      ? values[property.key]
      : getNestedValue(values, property.key);

    const getSafeValue = (val) => {
      if (val === undefined || val === null) {
        return '';
      }
      return String(val);
    };

    switch (property.type) {
      case 'boolean':
        return (
          <select
            value={getSafeValue(value)}
            onChange={e => handleValueChange(property.key, e.target.value === 'true')}
            className="form-select"
            disabled={property.readonly}
          >
            <option value="true">YES</option>
            <option value="false">NO</option>
          </select>
        )

      case 'select':
        return (
          <select
            value={getSafeValue(value)}
            onChange={e => handleValueChange(property.key, e.target.value)}
            className="form-select"
            disabled={property.readonly}
          >
            {property.options?.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )

      case 'number':
        return (
          <input
            type="number"
            value={getSafeValue(value)}
            onChange={e => {
              let val = Number(e.target.value);

              if (property.max !== undefined) {
                const max = Number(property.max)
                if (val > max) val = max;
              }
              
              handleValueChange(property.key, val);
            }}
            onBlur={(e) => {
              let val = Number(e.target.value);
        
              if (property.min !== undefined) {
                const min = Number(property.min);
                if (val < min) val = min;
              }
          
              handleValueChange(property.key, val);
            }}
            min={property.min}
            max={property.max}
            className="form-input"
            disabled={property.readonly}
          />
        )

      case 'duration':
        const { min, max } = DurationInputService.getConstraints(property.key);
        return (
          <DurationInput
            value={getSafeValue(value)}
            min={min}
            max={max}
            onChange={val => handleValueChange(property.key, val)}
            disabled={property.readonly}
            className="form-input"
          />
        )

      case 'tel':
        return (
          <input
            type="tel"
            value={getSafeValue(value)}
            onChange={e => handleValueChange(property.key, e.target.value)}
            placeholder={property.placeholder}
            className="form-input"
            disabled={property.readonly}
          />
        )

      case 'password':
        return (
          <input
            type="password"
            value={getSafeValue(value)}
            onChange={e => handleValueChange(property.key, e.target.value)}
            className="form-input"
            disabled={property.readonly}
          />
        )

      case 'text':
      default:
        return (
          <input
            type="text"
            value={getSafeValue(value)}
            onChange={e => handleValueChange(property.key, e.target.value)}
            placeholder={property.placeholder}
            className="form-input"
            disabled={property.readonly}
          />
        )
    }
  }

  return (
    <form onSubmit={handleSubmit} className="configuration-form">
      <div className="form-header">
        <h2>Create New Configuration</h2>
        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Save Configuration
          </button>
        </div>
      </div>

      
      <div className="form-basic-info">
        <div className="form-field-group">
          <label>Configuration Name *</label>
          <input
            type="text"
            value={configName}
            onChange={e => setConfigName(e.target.value)}
            className="form-input"
            placeholder="Enter configuration name"
            required
          />
        </div>
        <div className="form-field-group">
          <label>Description</label>
          <textarea
            value={configDescription}
            onChange={e => setConfigDescription(e.target.value)}
            className="form-input form-textarea"
            placeholder="Enter configuration description"
            rows={3}
          />
        </div>
      </div>

      <div className="configuration-sections">
        {configLayout.sections.map(section => {
          const visibleProperties = section.properties.filter(property =>
            hasInternalAccess || !INTERNAL_USER_ONLY_FIELDS.includes(property.key)
          )

          if (visibleProperties.length === 0) return null

          return (
            <div key={section.title} className="section-item">
              <button
                type="button"
                className="section-header"
                onClick={() => toggleSection(section.title)}
              >
                <span>{section.title}</span>
                <span className="section-toggle">{expandedSections.has(section.title) ? '−' : '+'}</span>
              </button>
              {expandedSections.has(section.title) && (
                <div className="section-content">
                  <div className="form-grid">
                    {visibleProperties.map(property => (
                      <div key={property.key} className="form-field-group">
                        <label>
                          {property.name}
                          {property.readonly && <span className="readonly-label"> (Read Only)</span>}
                        </label>
                        {renderField(property)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </form>
  )
}

export default function CreateConfigurationPage() {
  const navigate = useNavigate()
  const configDefaults = useMemo(() => extractDefaultValues(schema), [])

  const [showForm, setShowForm] = useState(false)
  const [showExistingModal, setShowExistingModal] = useState(false)
  const [selectedConfigValues, setSelectedConfigValues] = useState<DeviceConfigurationValues | undefined>()
  const [existingConfigurations, setExistingConfigurations] = useState<Configuration[]>([])
  const [loadingConfigurations, setLoadingConfigurations] = useState(false)

  const hasInternalAccess = authService.hasPermission(RESOURCES.DEFAULT_CONFIG, PermissionScope.CREATE);

  const handleCreateNew = () => {
    setSelectedConfigValues(undefined)
    setShowForm(true)
  }

  const handleCreateFromExisting = async () => {
    setShowExistingModal(true)
    setLoadingConfigurations(true)

    try {
      const configs = (await PartnerConfigurationService.getAll()).configurations
      setExistingConfigurations(configs)
    } catch (error) {
      toast.error('Failed to load configurations')
    } finally {
      setLoadingConfigurations(false)
    }
  }

  const handleCloseModal = () => {
    setShowExistingModal(false)
  }

  const handleSelectConfiguration = (configId: string) => {
    const selectedConfig = existingConfigurations.find(c => c.id === configId)
    if (selectedConfig) {
      const configValues = PartnerConfigurationService.jsonToConfigValues(selectedConfig.settingsJson)
      setSelectedConfigValues({ ...configDefaults, ...configValues })
      setShowExistingModal(false)
      setShowForm(true)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setSelectedConfigValues(undefined)
  }

  const handleSave = async (name: string, description: string, values: DeviceConfigurationValues) => {
    try {

      const nestedValues = unflattenConfiguration(values)
  
      const settingsJson = JSON.stringify(nestedValues, null, 2)
      
      await PartnerConfigurationService.create(name, settingsJson, description);
      toast.success('Configuration has successfully been created.');
      navigate(PAGES.VIEW_CONFIGURATION.route)
    } catch (error) {
      toast.error('Failed to save configuration.');
    }
  }

  if (showForm) {
    return (
      <div className="page-container">
        <Navbar />
        <main className="page-main-content">
          <HeaderBar />
          <div className="page-content">
            <ConfigurationForm
              initialValues={selectedConfigValues}
              onCancel={handleCancel}
              onSave={handleSave}
              hasInternalAccess={hasInternalAccess}
            />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="page-container">
      <Helmet>
        <title>{PAGES.CREATE_CONFIGURATION.title}</title>
      </Helmet>
      <Navbar />
      <main className="page-main-content">
        <HeaderBar />
        <div className="page-content">
          <h2>Create Configuration</h2>

          <div className="create-options">
            <div className="create-option-card">
              <h3>Create New Configuration</h3>
              <p>Start a new configuration from default values.</p>
              <button
                className="create-option-button"
                onClick={handleCreateNew}
              >
                Create
              </button>
            </div>

            <div className="create-option-card">
              <h3>Create Using Existing Configuration</h3>
              <p>Use an existing configuration as a template and modify it to suit your needs.</p>
              <button
                className="create-option-button secondary"
                onClick={handleCreateFromExisting}
              >
                Create Using Existing Configuration
              </button>
            </div>
          </div>
        </div>
      </main>

      {showExistingModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Existing Configuration</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              {loadingConfigurations ? (
                <div className="loading-message">Loading configurations...</div>
              ) : existingConfigurations.length === 0 ? (
                <div className="no-configurations-message">
                  <p>No configurations found. Create a new configuration to get started.</p>
                </div>
              ) : (
                <div className="existing-configurations-list">
                  {existingConfigurations.map(config => (
                    <div
                      key={config.id}
                      className="existing-config-item"
                      onClick={() => handleSelectConfiguration(config.id)}
                    >
                      <h4>{config.name}</h4>
                      {config.description && <p>{config.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
